/* eslint-disable consistent-return, default-case */

import FlashMessage from '@gameday/react-components/components/FlashMessage'
import { get } from 'lodash'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { amountDisplay } from '../../../../formats'
import PaymentGateway from '../../Common/PaymentGateway'
import BraintreeComponent from './braintree'
import PaypalComponent from './paypal'
import StripeComponent from './stripe'
import styles from './styles.scss'

const StyledGateway = styled.div`
  .MuiRadio-colorPrimary.Mui-checked {
    color: ${props => props.buttonColour};
  }
  .radio-advanced:hover,
  .radio-advanced:focus,
  .radio-advanced.active {
    border-color: ${props => props.buttonColour};
  }
  braintree-large-button braintree-toggle span {
    color: ${props => props.buttonColour};
  }
`

const PaymentComponent = ({
  paymentData,
  user,
  organisation,
  clearRegistrations,
  validateSessions,
  sessions,
  history,
  next,
  paymentPageError,
  paymentMethod,
  postPayment,
  showLoadingIndicator,
  setLoadingIndicatorContent,
  paymentGateway,
  setPaymentMethod,
  setPaymentPageError,
  buttonColour,
  buttonTextColour,
  // braintree
  braintreeToken,
  getBraintreeToken,
  braintreeId,
  braintreePaymentMethods,
  getBTPaymentMethods,
  deleteBTPaymentMethod,
  isPaymentSuccessful,
  isGuestSignup,
  formType,
  formSettings,
  userData,
  orderId,
  stripeData,
  createStripePaymentIntent,
  paymentSuccessful,
  goto,
  successPage,
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  ...restProps
}) => {
  const [paymentResult, setPaymentResult] = useState(null)
  const [paymentError, setPaymentError] = useState(null)
  const [chooseGateway, setChooseGateway] = useState(false)
  const { total = {}, fees = {} } = paymentData
  const { userName, email, phone, userid } = user
  const { name: organisationName, sfid: organisationId, softDescriptor } = organisation
  const { formId } = formSettings

  useEffect(() => {
    // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
    const historyListener = history.listen((location, action) => {
      if (action === 'POP') {
        clearRegistrations()
      }
    })
    return () => historyListener()
  }, [])

  useEffect(() => {
    if (!paymentResult) {
      return
    }

    switch (paymentMethod.type) {
      case 'braintree':
      case 'paypal': {
        const { nonce, type, saveForLater, paymentToken } = paymentResult

        let paymentAmount = total.rawValue
        if (paymentData.insurance?.rawValue) {
          paymentAmount = paymentAmount + (paymentData.insurance?.rawValue || 0)
        }

        validateSessions({
          sessions: { ...sessions, source: 'Payment' },
          paymentDetails: {
            amount: paymentAmount,
            paymentMethodNonce: nonce,
            paymentToken,
            paymentType: type,
            firstName: userName,
            lastName: userName,
            email,
            phone,
            organisationName,
            organisation: organisationId,
            descriptor: softDescriptor.substring(0, 18),
            userId: userid,
            saveForLater: paymentMethod.type === 'braintree' && type !== 'CreditCard' ? false : saveForLater,
          },
          next,
        })
        break
      }
    }
  }, [paymentResult])

  useEffect(() => {
    if (!isPaymentSuccessful) {
      onError()
    } else {
      clearError()
    }
  }, [isPaymentSuccessful])

  const onError = (title, message) => {
    setPaymentError({
      title: title || 'An error occurred on payment',
      message: message || 'Try again or select another payment method.',
    })
  }

  const clearError = () => {
    setPaymentPageError(null)
    setPaymentError(null)
  }

  const changeGateway = () => {
    setPaymentPageError(null)
    setPaymentError(null)
    setChooseGateway(true)
  }

  const renderChooseGateway = () => (
    <PaymentGateway
      total={Number(paymentData.total.rawValue)}
      setPaymentMethod={setPaymentMethod}
      gateways={paymentGateway}
      onGatewayChange={gateway => {
        setPaymentMethod(gateway)
        setChooseGateway(false)
      }}
      noDefault
    />
  )

  const renderGateway = () => {
    let paymentAmount = paymentData.total.rawValue
    if (paymentData.insurance?.rawValue) {
      paymentAmount = paymentAmount + paymentData.insurance.rawValue || 0
    }

    switch (paymentMethod.type) {
      case 'stripe':
        return (
          <StripeComponent
            onError={onError}
            onChangeGateway={() => changeGateway()}
            showLoadingIndicator={showLoadingIndicator}
            buttonColour={buttonColour}
            buttonTextColour={buttonTextColour}
            orderId={orderId}
            createPaymentIntent={createStripePaymentIntent}
            stripeData={stripeData}
            returnUrl={`${window.location.origin}/#payment-complete?orgId=${organisationId}&formId=${formId}`}
            onSuccess={() => {
              paymentSuccessful(true)
              goto(successPage)
            }}
          />
        )
      case 'braintree':
        return (
          <BraintreeComponent
            clientToken={braintreeToken}
            getClientToken={getBraintreeToken}
            onSuccess={setPaymentResult}
            onError={onError}
            braintreeId={braintreeId}
            onChangeGateway={() => changeGateway()}
            currency={paymentMethod.currency}
            total={paymentAmount}
            isGuestSignup={isGuestSignup}
            formType={formType}
            buttonColour={buttonColour}
            buttonTextColour={buttonTextColour}
            userData={userData}
            is3DSEnabled={
              get(
                (paymentGateway || []).find(pg => pg.name.toLowerCase() === 'braintree'),
                'is3DSEnabled'
              ) || false
            }
          />
        )
      case 'paypal':
        return (
          <PaypalComponent
            clientToken={braintreeToken}
            clearError={clearError}
            getClientToken={getBraintreeToken}
            onSuccess={setPaymentResult}
            onError={onError}
            braintreeId={braintreeId}
            currency={paymentMethod.currency}
            total={paymentAmount}
            showLoadingIndicator={showLoadingIndicator}
            onChangeGateway={() => changeGateway()}
            paymentMethods={braintreePaymentMethods}
            getPaymentMethods={getBTPaymentMethods}
            deletePayPalToken={deleteBTPaymentMethod}
            isFetchFailed={
              // biome-ignore lint/complexity/useOptionalChain: TODO
              get(paymentPageError, 'description') && get(paymentPageError, 'description').includes('fetching')
            }
            isGuestSignup={isGuestSignup}
            buttonColour={buttonColour}
            buttonTextColour={buttonTextColour}
          />
        )
    }
  }

  let displayAmount = total.value
  if (paymentMethod.type === 'stripe' && stripeData) {
    displayAmount = stripeData.amountFormatted
  } else {
    if (paymentData.insurance?.rawValue) {
      const newAmount = paymentData.total.rawValue + (paymentData.insurance?.rawValue || 0)
      displayAmount = amountDisplay(newAmount, paymentData.currencyCode)
    }
  }

  return (
    <>
      {(paymentError || get(paymentPageError, 'title')) && (
        <div style={{ marginBottom: '1rem' }}>
          <FlashMessage
            type="error"
            showInterval={0}
            title={get(paymentError, 'title') || get(paymentPageError, 'title')}
          >
            <span key="text" className={styles.alertBody}>
              {get(paymentError, 'message') || get(paymentPageError, 'description')}
            </span>
          </FlashMessage>
        </div>
      )}
      <h2>Process Payment</h2>
      {typeof displayAmount !== 'undefined' && (
        <strong className={styles.totalAmount}>{`Total due: ${displayAmount}`}</strong>
      )}
      {fees.rawValue !== 0 && (
        <small className={styles.transactionFees}>{`Includes Total Processing Fee ${fees.value}`}</small>
      )}
      <StyledGateway buttonColour={buttonColour}>
        {chooseGateway ? renderChooseGateway() : renderGateway()}
      </StyledGateway>
    </>
  )
}

export default PaymentComponent
